import { useAppDispatch, useAppSelector } from "ui/hook/store";
import {
  HiAcademicCap,
  HiArrowLeft,
  HiHeart,
  HiOutlineHeart,
} from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { translate } from "ui/i18n";
import { useWindowWidth } from "ui/utils/hooks";
import { selectFavoriteTutorials } from "domain/activities/activities.selectors";
import { toggleActivityFavorite } from "ui/utils/toggle-activity-favorite";
import { useEffect, useState } from "react";
import { selectTutorial } from "domain/tutorials/tutorials.selectors";
import {
  getTutorial,
  getTutorialByInnizRef,
} from "domain/tutorials/tutorials.actions";
import { TutorialBanner } from "ui/pages/app/tutorials/tutorial/TutorialBanner";
import { TutorialType } from "domain/entities/tutorials.entities";
import Vimeo from "@u-wave/react-vimeo";
import { selectIsPremium } from "domain/auth/auth.selectors";

export function Tutorial({
  selectedTutorialId,
  innizRef,
}: {
  selectedTutorialId: string | null;
  innizRef?: string | null;
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isDesktop = useWindowWidth();
  const tutorial = useAppSelector(selectTutorial);
  const favoriteTutorial = useAppSelector(selectFavoriteTutorials).find(
    (t) => t.tutorialId === tutorial?.id
  );
  const [isLoading, setIsLoading] = useState(true);
  const isPremium = useAppSelector(selectIsPremium);

  useEffect(() => {
    if (selectedTutorialId || innizRef) {
      fetchInfos();
    }
  }, [selectedTutorialId]); //eslint-disable-line

  const fetchInfos = async () => {
    const { success, tutorial } = await dispatch(
      selectedTutorialId
        ? getTutorial(Number(selectedTutorialId))
        : getTutorialByInnizRef(Number(innizRef))
    ).unwrap();
    if (!success || (tutorial?.premium && !isPremium)) {
      navigate("/tutorials");
    }
    setIsLoading(false);
  };

  const handleFavoriteClick = async (e: any) => {
    e.stopPropagation();
    toggleActivityFavorite({
      favoriteActivity: favoriteTutorial,
      dispatch,
      activityId: tutorial.id,
      activityType: "tutorial",
    });
  };

  return (
    <div className="flex h-full flex-col bg-darkGrey-100 p-4 md:flex-row md:py-8 md:pl-6 md:pr-10">
      <div className="mb-4 flex w-full items-center justify-between md:my-0 md:w-12 md:items-start">
        <button
          onClick={() => navigate(-1)}
          className="z-50 flex h-5 items-center"
        >
          <HiArrowLeft size={24} className="text-darkGrey-800" />
        </button>
        {!isDesktop && (
          <div className="flex w-fit space-x-4">
            <button
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
              onClick={handleFavoriteClick}
            >
              {!!favoriteTutorial?.id ? (
                <HiHeart className="text-secondaryGrey-900" size={20} />
              ) : (
                <HiOutlineHeart className="text-secondaryGrey-900" size={20} />
              )}
            </button>
          </div>
        )}
      </div>

      {tutorial && !isLoading ? (
        <>
          <div className="flex w-full flex-col space-y-6 md:space-y-8">
            <TutorialBanner
              tutorial={tutorial}
              isFavorite={!!favoriteTutorial?.id}
              handleFavoriteClick={handleFavoriteClick}
            />

            <div className="flex flex-wrap gap-x-3 gap-y-3 md:justify-start">
              <div className="flex h-7 items-center rounded-[36px] bg-darkGrey-700 px-3 text-xs font-semibold text-white">
                {translate(
                  `ns1:Tutorials.Types.${tutorial.series as TutorialType}`
                )}
              </div>
              {/* <div className="flex h-7 items-center rounded-[36px] bg-darkGrey-200 px-3 text-xs font-semibold text-darkGrey-700">
                {getDurationInHours(Number(technique.duration))}
              </div> */}
            </div>

            <div className="flex flex-col space-x-0 space-y-6 md:flex-row md:space-x-8 md:space-y-0">
              <div className="flex w-full flex-col space-y-6 md:space-y-8">
                <div className="rounded-[30px] bg-white p-4 py-6 md:p-6">
                  <div
                    className="flex w-full justify-center overflow-hidden"
                    style={{ backgroundColor: "#000" }}
                  >
                    <Vimeo
                      video={tutorial.videoUrl}
                      style={{ maxWidth: "1000px", aspectRatio: "16/9" }}
                      className="vimeo-player h-full w-full"
                    />
                  </div>

                  <div className="mt-6 font-bold md:mt-10">
                    {translate("ns1:Tutorial.About")}
                  </div>
                  <div className="mt-4 text-sm leading-[24px] md:mt-6 md:text-base">
                    {tutorial.content}
                  </div>
                </div>
              </div>

              {/* serie explanation */}
              <div className="flex flex-col space-y-6 md:w-80 md:space-y-8">
                <div className="space-y-3 rounded-[30px] bg-white p-6">
                  <HiAcademicCap size={46} color="#47548C" />
                  <p className="text-xl font-bold text-secondaryGrey-900 md:text-2xl">
                    {translate(
                      `ns1:Tutorial.Series.${tutorial.series as TutorialType}`
                    )}
                  </p>
                  <div className="text-sm font-medium text-secondaryGrey-700 md:text-base">
                    {translate(
                      `ns1:Tutorial.SerieExplanations.${
                        tutorial.series as TutorialType
                      }`
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <div className="loader" />
        </div>
      )}
    </div>
  );
}
